import { h, } from "@stencil/core";
/**
 * A modal to confirm an action.
 * @part preheading - The h2 above the heading.
 * @part heading - The h1 heading.
 * @part body - The body of the modal.
 * @part warning - The warning text (if provided).
 * @part type_to_confirm - The type to confirm label (if enabled).
 * @part cancel - The cancel button.
 * @part confirm - The confirm button.
 */
export class ConfirmModal {
    constructor() {
        this.clickPre = async () => {
            if (!this.typeToConfirm)
                return;
            try {
                await navigator.clipboard.writeText(this.typeToConfirm);
                if (this.copied)
                    return;
                this.copied = true;
                clearTimeout(this.removeCopiedTimeout);
                this.removeCopiedTimeout = setTimeout(() => {
                    this.copied = false;
                }, 1000);
            }
            catch (_error) {
                // oh well
            }
        };
        this.onInputChange = (e) => {
            const input = e.target;
            this.typed = input.value;
        };
        this.confirmDisabled = () => {
            if (!this.typeToConfirm)
                return false;
            return !!this.typeToConfirm.localeCompare(this.typed, 'en', {
                sensitivity: 'base',
            });
        };
        this.preHeading = undefined;
        this.heading = undefined;
        this.body = undefined;
        this.warning = undefined;
        this.confirm = undefined;
        this.confirmVariant = 'delete';
        this.typeToConfirm = undefined;
        this.typed = '';
        this.copied = false;
    }
    render() {
        return (h("es-modal", { key: '35a2484193a40d44bdcc4e79e15034e615cbd375', role: 'alert' }, h("h2", { key: '7060dcb669c1c2ebdb78d96b6906536c5848d8d2', slot: 'header', part: 'preheading' }, this.preHeading), h("h1", { key: '5a0b8b8aa060dcc7f552c09d414cfb72e2aacf59', slot: 'header', part: 'heading' }, this.heading), h("p", { key: '276deac60a1a01722d053624edee3799cc7bbde3', part: 'body' }, typeof this.body === 'string' ? this.body : h(this.body)), !!this.warning && (h("p", { key: 'd50f36a8a72ff67e51d3ab909a324f0114a0ed43', class: 'important', part: 'warning' }, this.warning)), !!this.typeToConfirm && (h("label", { key: '6cbadeaeaa6f8c6ff37fdc7dd7d27c44cb6adeca', class: 'type_to_confirm', part: 'type_to_confirm' }, 'To confirm type ', h("pre", { key: 'c98af6e1eae2c163cb33e588478a86076881e76f', onClick: this.clickPre }, this.typeToConfirm, h("es-popover", { key: 'b333b88b1442298b7306aec1e719bbc10aca2e93', open: this.copied }, 'Copied to clipboard')), ' in the box below.', h("input", { key: '5b1a2d47235dcd38f0280ede248840a1b246735b', type: 'text', name: 'type_to_confirm', placeholder: this.typeToConfirm, onInput: this.onInputChange, value: this.typed }))), h("es-button", { key: '093f64726ea6beec5ee31e1cfb0f055d4538c4e4', variant: 'cancel', slot: 'footer', part: 'cancel', onClick: this.requestClose.emit }, 'Cancel'), h("es-button", { key: 'd38787f013ab63357b9dd4435036c6c6bfbd3ff7', variant: this.confirmVariant, slot: 'footer', part: 'confirm', onClick: this.requestDeletion.emit, disabled: this.confirmDisabled() }, this.confirm)));
    }
    static get is() { return "es-confirm-modal"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["es-confirm-modal.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["es-confirm-modal.css"]
        };
    }
    static get properties() {
        return {
            "preHeading": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Text to display above the heading."
                },
                "attribute": "pre-heading",
                "reflect": false
            },
            "heading": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Text to display in the heading."
                },
                "attribute": "heading",
                "reflect": false
            },
            "body": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | FunctionalComponent",
                    "resolved": "FunctionalComponent<{}> | string",
                    "references": {
                        "FunctionalComponent": {
                            "location": "import",
                            "path": "@stencil/core",
                            "id": "../../node_modules/@stencil/core/internal/stencil-core/index.d.ts::FunctionalComponent"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Text or component to display in the body of the modal."
                },
                "attribute": "body",
                "reflect": false
            },
            "warning": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Text to display in red below the body."
                },
                "attribute": "warning",
                "reflect": false
            },
            "confirm": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Text to display within the confirm button."
                },
                "attribute": "confirm",
                "reflect": false
            },
            "confirmVariant": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ButtonVariant",
                    "resolved": "\"cancel\" | \"default\" | \"delete\" | \"filled\" | \"link\" | \"minimal\" | \"outline\"",
                    "references": {
                        "ButtonVariant": {
                            "location": "import",
                            "path": "../../buttons/types",
                            "id": "src/components/buttons/types.ts::ButtonVariant"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Button variant for the confirm button."
                },
                "attribute": "confirm-variant",
                "reflect": false,
                "defaultValue": "'delete'"
            },
            "typeToConfirm": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "String required to be typed to enable the confirm button."
                },
                "attribute": "type-to-confirm",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "typed": {},
            "copied": {}
        };
    }
    static get events() {
        return [{
                "method": "requestClose",
                "name": "requestClose",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Triggered when the user has indicated that they want to close the modal."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "requestDeletion",
                "name": "requestDeletion",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Triggered when the user has indicated that they want to close the modal."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
}
//# sourceMappingURL=es-confirm-modal.js.map
